import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Camera, CheckCircle2, Loader2, Save, UserRound } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5 MB cap on raw input
const MAX_DIMENSION = 512;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const AUTO_CLOSE_MS = 1500;

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(new Error('Failed to read image file'));
  reader.readAsDataURL(file);
});

const compressImage = async (file) => {
  const source = await readAsDataUrl(file);

  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load selected image'));
    img.src = source;
  });

  const scale = Math.min(1, MAX_DIMENSION / Math.max(image.width, image.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));

  const context = canvas.getContext('2d');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.84);
};

const normalizeTelegram = (value) => (value || '').trim().replace(/^@+/, '');

const ProfileSettingsModal = ({ isOpen, onClose }) => {
  const { user, persistUser } = useAuth();
  const fileInputRef = useRef(null);
  const closeTimerRef = useRef(null);

  const [displayName, setDisplayName] = useState('');
  const [telegramId, setTelegramId] = useState('');
  const [profilePic, setProfilePic] = useState('');
  const [isPreparingImage, setIsPreparingImage] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Initial values to detect changes
  const [initialName, setInitialName] = useState('');
  const [initialTelegram, setInitialTelegram] = useState('');
  const [initialPic, setInitialPic] = useState('');

  // Keep latest user in a ref so we can populate on open without re-running
  // the init effect every time the user object refreshes (AuthContext polls
  // /users/{id} every 7s, which otherwise wipes user's in-progress edits).
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    if (!isOpen) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      return;
    }

    const current = userRef.current;
    if (!current) return;

    const name = current.name || '';
    const tg = current.telegramId || '';
    const pic = current.profilePic || '';

    setDisplayName(name);
    setTelegramId(tg);
    setProfilePic(pic);
    setInitialName(name);
    setInitialTelegram(tg);
    setInitialPic(pic);
    setMessage('');
    setError('');
    setIsPreparingImage(false);
    setIsSaving(false);
  }, [isOpen]);

  const initials = useMemo(() => {
    const value = (displayName || user?.name || 'H').trim();
    return value.slice(0, 1).toUpperCase();
  }, [displayName, user?.name]);

  // Android WebView can ignore programmatic clicks on display:none file inputs.
  // The upload control is rendered as a <label htmlFor=...> below so the file
  // chooser is opened by a direct user gesture inside the APK while browser
  // behavior stays native.

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError('');
    setMessage('');

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setError('Please choose a JPG or PNG image.');
      event.target.value = '';
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      setError('Please choose an image smaller than 5 MB.');
      event.target.value = '';
      return;
    }

    try {
      setIsPreparingImage(true);
      const compressed = await compressImage(file);
      setProfilePic(compressed);
      setMessage('Image ready. Press Save to apply changes.');
    } catch (imageError) {
      setError(imageError?.message || 'Failed to prepare image.');
    } finally {
      setIsPreparingImage(false);
      event.target.value = '';
    }
  };

  const handleSave = async () => {
    if (!user?.id || isSaving) return;

    // Build payload with only changed fields
    const trimmedName = (displayName || '').trim();
    const trimmedTg = (telegramId || '').trim();

    const payload = {};

    if (trimmedName !== (initialName || '').trim()) {
      if (!trimmedName) {
        setError('Display name cannot be empty.');
        return;
      }
      payload.name = trimmedName;
    }

    if (normalizeTelegram(telegramId) !== normalizeTelegram(initialTelegram)) {
      // Allow clearing by sending empty string
      payload.telegramId = trimmedTg;
    }

    if ((profilePic || '') !== (initialPic || '')) {
      payload.profilePic = profilePic;
    }

    if (Object.keys(payload).length === 0) {
      setMessage('No changes to save.');
      return;
    }

    setIsSaving(true);
    setError('');
    setMessage('');

    try {
      const { data } = await api.updateUserProfile(user.id, payload);
      const updatedUser = data?.user;

      // Instantly reflect updated user in global auth state (no page reload needed)
      if (updatedUser && persistUser) {
        persistUser(updatedUser);
        // Sync local initial baseline so subsequent saves detect only new changes
        setInitialName(updatedUser.name || '');
        setInitialTelegram(updatedUser.telegramId || '');
        setInitialPic(updatedUser.profilePic || '');
        setDisplayName(updatedUser.name || '');
        setTelegramId(updatedUser.telegramId || '');
        setProfilePic(updatedUser.profilePic || '');
      }

      setMessage('Updated');

      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      closeTimerRef.current = setTimeout(() => {
        onClose?.();
      }, AUTO_CLOSE_MS);
    } catch (saveError) {
      const detail = saveError?.response?.data?.detail;
      setError(detail || saveError?.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDialogOpenChange = (next) => {
    if (isSaving) return;
    if (!next) {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      onClose?.();
    }
  };

  const uploadDisabled = isPreparingImage || isSaving;

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent className="max-w-md glass-panel border-white/40 max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-2xl text-[#000000]">Profile Settings</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="flex flex-col items-center gap-3">
            <div className="relative h-24 w-24 overflow-hidden rounded-full border-4 border-white shadow-lg shadow-[#9333EA]/10">
              {profilePic ? (
                <img
                  src={profilePic}
                  alt={displayName || user?.name || 'Profile'}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#9333EA] to-[#EC4899] text-2xl font-bold text-white">
                  {initials}
                </div>
              )}
              {isPreparingImage && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <Loader2 className="h-6 w-6 animate-spin text-white" />
                </div>
              )}
            </div>
            <div
              aria-disabled={uploadDisabled}
              data-testid="profile-pic-upload-btn"
              onClick={(event) => {
                if (uploadDisabled) {
                  event.preventDefault();
                  return;
                }
                if (event.target !== fileInputRef.current) {
                  fileInputRef.current?.click();
                }
              }}
              className="relative inline-flex cursor-pointer overflow-hidden rounded-full border border-[#9333EA]/20 bg-white/70 text-sm font-semibold text-[#9333EA] focus-within:outline-none focus-within:ring-2 focus-within:ring-[#9333EA]/30 aria-disabled:cursor-not-allowed aria-disabled:opacity-70"
            >
              <input
                id="profile-pic-input"
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                disabled={uploadDisabled}
                aria-label="Upload Profile Picture"
                className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
                onChange={handleImageChange}
                data-testid="profile-pic-input"
              />
              <span className="pointer-events-none relative z-0 inline-flex items-center gap-2 px-4 py-2">
                {isPreparingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
                {isPreparingImage ? 'Preparing...' : 'Upload Profile Picture'}
              </span>
            </div>
            <p className="text-center text-xs text-[#666666] font-['DM_Sans']">
              Choose a JPG or PNG photo (up to 5 MB). We will optimize it before saving.
            </p>
          </div>

          <div className="space-y-3">
            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-[#000000] font-['DM_Sans']">Display Name</span>
              <div className="relative">
                <UserRound className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9333EA]" />
                <input
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  placeholder="Enter your display name"
                  maxLength={60}
                  data-testid="profile-display-name-input"
                  className="h-12 w-full rounded-[16px] border border-white/60 bg-white/80 pl-11 pr-4 text-sm text-[#000000] outline-none focus:border-[#9333EA]/40"
                />
              </div>
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-semibold text-[#000000] font-['DM_Sans']">Telegram ID</span>
              <input
                value={telegramId}
                onChange={(event) => setTelegramId(event.target.value)}
                placeholder="@yourtelegramid"
                maxLength={64}
                data-testid="profile-telegram-input"
                className="h-12 w-full rounded-[16px] border border-white/60 bg-white/80 px-4 text-sm text-[#000000] outline-none focus:border-[#9333EA]/40"
              />
              <span className="mt-1 block text-xs text-[#666666] font-['DM_Sans']">
                4–32 characters: letters, numbers, underscore. Leave empty to remove.
              </span>
            </label>
          </div>

          {(message || error) && (
            <div
              data-testid="profile-settings-feedback"
              className={`flex items-center gap-2 rounded-[16px] px-4 py-3 text-sm font-['DM_Sans'] ${
                error
                  ? 'bg-red-50 text-red-600 border border-red-100'
                  : message === 'Updated'
                  ? 'bg-green-50 text-green-700 border border-green-100'
                  : 'bg-[#F5F3FF] text-[#6D28D9] border border-[#EDE9FE]'
              }`}
            >
              {!error && message === 'Updated' && <CheckCircle2 className="h-4 w-4" />}
              <span>{error || message}</span>
            </div>
          )}

          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving || isPreparingImage}
            data-testid="profile-settings-save-btn"
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#9333EA] via-[#EC4899] to-[#EF4444] text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileSettingsModal;
